import { useEffect, useRef, useState } from 'react'
import { Button, Card, CardContent, cn } from '@saas-pages/shared/ui'
import { toolError, toolInvoke, toolSuccess } from '@saas-pages/shared/public/runtime'
import { format, useFormatBytes, useT, type Translations } from '@/shared/i18n'
import { getUtilityDefinition } from '@/shared/utilities/utilities'
import {
  MAX_FILE_MB,
  UnlockPdfError,
  unlockPdf,
  unlockedPdfFilename,
  validateFileSize,
} from './lib'
import { FilePickPrompt } from '@/shared/ui/FilePickPrompt'

const UTILITY = getUtilityDefinition('unlockPdf').analyticsKey

type Phase =
  | { kind: 'idle' }
  | { kind: 'ready'; file: File }
  | { kind: 'loading'; file: File }
  | { kind: 'success'; file: File; blob: Blob; objectUrl: string; filename: string }
  | { kind: 'error'; file: File | null; message: string; errorCode: string }

interface DropZoneProps {
  onFile: (file: File) => void
  disabled: boolean
}

function DropZone({ onFile, disabled }: DropZoneProps) {
  const t = useT()
  const inputRef = useRef<HTMLInputElement>(null)
  const [dragging, setDragging] = useState(false)

  function handleFiles(files: FileList | null) {
    if (!files || files.length === 0) return
    const file = files[0]
    if (!file || !file.name.toLowerCase().endsWith('.pdf')) return
    onFile(file)
  }

  return (
    <div
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-label={t.common.pickPdf}
      onClick={() => !disabled && inputRef.current?.click()}
      onKeyDown={(event) => {
        if (!disabled && (event.key === 'Enter' || event.key === ' ')) {
          inputRef.current?.click()
        }
      }}
      onDragOver={(event) => {
        event.preventDefault()
        if (!disabled) setDragging(true)
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={(event) => {
        event.preventDefault()
        setDragging(false)
        if (!disabled) handleFiles(event.dataTransfer.files)
      }}
      className={cn(
        'flex cursor-pointer select-none flex-col items-center justify-center gap-3',
        'rounded-xl border-2 border-dashed p-6 sm:p-10 text-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 transition-colors',
        dragging && !disabled
          ? 'border-primary bg-primary/5'
          : 'border-border hover:border-primary/50 hover:bg-accent/30',
        disabled && 'pointer-events-none opacity-50',
      )}
    >
      <input
        ref={inputRef}
        type="file"
        accept=".pdf,application/pdf"
        className="sr-only"
        tabIndex={-1}
        disabled={disabled}
        onChange={(event) => {
          handleFiles(event.target.files)
          event.currentTarget.value = ''
        }}
      />
      <div className="text-4xl" aria-hidden>
        🔓
      </div>
      <div>
        <p className="text-sm font-medium text-foreground">
          <FilePickPrompt drag={t.common.dragPdf} />
        </p>
        <p className="mt-1 text-xs text-muted-foreground">
          {format(t.common.pdfLimit, { size: MAX_FILE_MB })}
        </p>
        <p className="mt-1 text-[11px] text-muted-foreground/80">
          {t.common.localBadge}
        </p>
      </div>
    </div>
  )
}

interface FileBadgeProps {
  file: File
  onClear: () => void
  disabled: boolean
}

function FileBadge({ file, onClear, disabled }: FileBadgeProps) {
  const t = useT()
  const formatBytes = useFormatBytes()

  return (
    <div className="flex items-center gap-3 rounded-lg border bg-accent/30 px-4 py-3">
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary/10 text-sm font-semibold text-primary">
        PDF
      </div>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-foreground">{file.name}</p>
        <p className="text-xs text-muted-foreground">{formatBytes(file.size)}</p>
      </div>
      <button
        type="button"
        onClick={onClear}
        disabled={disabled}
        aria-label={t.common.deleteFile}
        className={cn(
          'shrink-0 rounded p-1 text-muted-foreground transition-colors',
          'hover:bg-destructive/10 hover:text-destructive',
          disabled && 'pointer-events-none opacity-50',
        )}
      >
        ×
      </button>
    </div>
  )
}

function Spinner() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="animate-spin"
      aria-hidden="true"
    >
      <path d="M21 12a9 9 0 1 1-6.219-8.56" />
    </svg>
  )
}

function ErrorPanel({ message, onRetry }: { message: string; onRetry: () => void }) {
  const t = useT()

  return (
    <Card className="border-destructive/30 bg-destructive/5">
      <CardContent className="pt-5">
        <p className="mb-2 text-sm font-semibold text-destructive">{t.common.errorTitle}</p>
        <p className="mb-4 text-sm text-foreground">{message}</p>
        <Button variant="outline" size="sm" onClick={onRetry}>
          {t.common.retry}
        </Button>
      </CardContent>
    </Card>
  )
}

interface ResultPanelProps {
  phase: Extract<Phase, { kind: 'success' }>
  onReset: () => void
}

function ResultPanel({ phase, onReset }: ResultPanelProps) {
  const t = useT()
  const formatBytes = useFormatBytes()

  return (
    <Card>
      <CardContent className="space-y-5 pt-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="text-sm font-semibold text-foreground">{t.common.done}</p>
            <p className="mt-1 text-xs text-muted-foreground">
              {format(t.unlockPdf.successDetails, { size: formatBytes(phase.blob.size) })}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button asChild>
              <a href={phase.objectUrl} download={phase.filename}>
                {t.unlockPdf.downloadPdf}
              </a>
            </Button>
            <Button type="button" variant="outline" onClick={onReset}>
              {t.unlockPdf.clear}
            </Button>
          </div>
        </div>
        <div className="rounded-lg border bg-muted/30 px-4 py-3 text-sm text-muted-foreground">
          {t.unlockPdf.resultNotice}
        </div>
      </CardContent>
    </Card>
  )
}

function errorMessage(error: unknown, t: Translations): { code: string; message: string } {
  if (error instanceof UnlockPdfError) {
    if (error.code === 'wrong_password') return { code: error.code, message: t.unlockPdf.wrongPassword }
    if (error.code === 'unsupported_encryption') {
      return { code: error.code, message: t.unlockPdf.unsupportedEncryption }
    }
    if (error.code === 'invalid_pdf') return { code: error.code, message: t.unlockPdf.invalidPdf }
    return { code: error.code, message: t.unlockPdf.decryptError }
  }
  return { code: 'decrypt_error', message: t.unlockPdf.decryptError }
}

export function SnyatParolSPdfForm() {
  const t = useT()
  const [phase, setPhase] = useState<Phase>({ kind: 'idle' })
  const [password, setPassword] = useState('')
  const resultUrlRef = useRef<string | null>(null)

  useEffect(() => {
    return () => {
      if (resultUrlRef.current) URL.revokeObjectURL(resultUrlRef.current)
    }
  }, [])

  const isLoading = phase.kind === 'loading'
  const currentFile =
    phase.kind === 'ready' || phase.kind === 'loading' || phase.kind === 'error'
      ? phase.file
      : null

  function revokeSuccessUrl() {
    if (resultUrlRef.current) {
      URL.revokeObjectURL(resultUrlRef.current)
      resultUrlRef.current = null
    }
  }

  function handleFile(file: File) {
    revokeSuccessUrl()
    if (!validateFileSize(file)) {
      toolError(UTILITY, 'client_too_large')
      setPhase({
        kind: 'error',
        file,
        errorCode: 'client_too_large',
        message: format(t.common.tooLarge, { size: MAX_FILE_MB }),
      })
      return
    }
    setPhase({ kind: 'ready', file })
  }

  function handleClear() {
    revokeSuccessUrl()
    setPassword('')
    setPhase({ kind: 'idle' })
  }

  function handleRetry() {
    if (phase.kind === 'error' && phase.file) {
      setPhase({ kind: 'ready', file: phase.file })
    } else {
      setPhase({ kind: 'idle' })
    }
  }

  async function handleSubmit() {
    if (phase.kind !== 'ready' || !password) return
    const file = phase.file
    const startMs = Date.now()

    setPhase({ kind: 'loading', file })
    toolInvoke(UTILITY, { input_bytes: file.size })

    try {
      const blob = await unlockPdf(file, password)
      const objectUrl = URL.createObjectURL(blob)
      const durationMs = Date.now() - startMs
      resultUrlRef.current = objectUrl

      toolSuccess(UTILITY, durationMs, {
        input_bytes: file.size,
        output_bytes: blob.size,
      })

      setPhase({
        kind: 'success',
        file,
        blob,
        objectUrl,
        filename: unlockedPdfFilename(file.name),
      })
    } catch (error: unknown) {
      const { code, message } = errorMessage(error, t)
      toolError(UTILITY, code, { input_bytes: file.size })
      setPhase({ kind: 'error', file, errorCode: code, message })
    }
  }

  if (phase.kind === 'success') {
    return <ResultPanel phase={phase} onReset={handleClear} />
  }

  return (
    <div className="space-y-5">
      {currentFile ? (
        <FileBadge file={currentFile} onClear={handleClear} disabled={isLoading} />
      ) : (
        <DropZone onFile={handleFile} disabled={isLoading} />
      )}

      <div className="rounded-lg border bg-muted/30 px-4 py-3 text-sm text-muted-foreground">
        {t.unlockPdf.knownPasswordNotice}
      </div>

      <label className="block space-y-2">
        <span className="text-sm font-medium text-foreground">{t.unlockPdf.passwordLabel}</span>
        <input
          type="password"
          value={password}
          disabled={!currentFile || isLoading}
          autoComplete="off"
          placeholder={t.unlockPdf.passwordPlaceholder}
          onChange={(event) => setPassword(event.target.value)}
          className={cn(
            'block w-full rounded-md border border-input bg-background px-3 py-2 text-sm',
            'placeholder:text-muted-foreground/70',
            'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
            (!currentFile || isLoading) && 'cursor-not-allowed opacity-60',
          )}
        />
      </label>

      {phase.kind === 'error' && <ErrorPanel message={phase.message} onRetry={handleRetry} />}

      <Button
        size="lg"
        className="w-full"
        disabled={phase.kind !== 'ready' || !password || isLoading}
        onClick={handleSubmit}
      >
        {isLoading ? (
          <>
            <Spinner />
            {t.unlockPdf.loading}
          </>
        ) : (
          t.unlockPdf.submit
        )}
      </Button>
    </div>
  )
}
